import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Paper,
    TextField,
    InputAdornment,
    Button,
    Grid,
    Card,
    CardContent,
    CardActions,
    Avatar,
} from "@mui/material";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import ReceiptIcon from "@mui/icons-material/Receipt";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import PersonIcon from "@mui/icons-material/Person";
import { toast } from "react-toastify";
import { REACT_APP_BACKEND_URL } from "../../Utils/Language/urlBase";
import "./customerReportsList.css";

const CustomerReportsList = () => {
    const [customers, setCustomers] = useState([]);
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const authHeader = useAuthHeader();
    const navigate = useNavigate();

    // Fetch customers
    useEffect(() => {
        const fetchCustomers = async () => {
            setIsLoading(true);
            try {
                const response = await fetch(
                    `${REACT_APP_BACKEND_URL}/otherparty/customers`,
                    {
                        headers: {
                            Authorization: authHeader,
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error("Failed to fetch customers");
                }

                const data = await response.json();
                if (data.success) {
                    setCustomers(data.data);
                    setFilteredCustomers(data.data);
                } else {
                    toast.error("Could not load customers");
                }
            } catch (error) {
                console.error("Error fetching customers:", error);
                toast.error("Error fetching customers");
            } finally {
                setIsLoading(false);
            }
        };

        fetchCustomers();
    }, [authHeader]);

    // Handle search
    const handleSearch = (event) => {
        const value = event.target.value.toLowerCase();
        setSearchTerm(value);

        if (value === "") {
            setFilteredCustomers(customers);
        } else {
            const filtered = customers.filter(
                (customer) =>
                    customer.name.toLowerCase().includes(value) ||
                    (customer.phone &&
                        customer.phone.toLowerCase().includes(value))
            );
            setFilteredCustomers(filtered);
        }
    };

    // Navigate to customer report
    const handleViewReport = (customerId) => {
        navigate(`/customer-report/${customerId}`);
    };

    // Get initials for avatar
    const getInitials = (name) => {
        return name
            .split(" ")
            .map((word) => word[0])
            .join("")
            .toUpperCase()
            .substring(0, 2);
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Customer Reports
            </Typography>

            <Paper sx={{ p: 3, mb: 4 }}>
                <Box sx={{ mb: 3 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        label="Search Customers"
                        value={searchTerm}
                        onChange={handleSearch}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        placeholder="Search by name or phone number"
                    />
                </Box>

                <Typography variant="subtitle1" gutterBottom>
                    {filteredCustomers.length} customers found
                </Typography>
            </Paper>

            <Grid container spacing={3}>
                {isLoading ? (
                    <Grid item xs={12}>
                        <Typography>Loading customers...</Typography>
                    </Grid>
                ) : filteredCustomers.length === 0 ? (
                    <Grid item xs={12}>
                        <Typography>
                            No customers found matching your search criteria.
                        </Typography>
                    </Grid>
                ) : (
                    filteredCustomers.map((customer) => (
                        <Grid item xs={12} sm={6} md={4} key={customer.id}>
                            <Card className="customer-card">
                                <CardContent>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            mb: 2,
                                        }}
                                    >
                                        <Avatar className="customer-avatar">
                                            {getInitials(customer.name)}
                                        </Avatar>
                                        <Typography variant="h6" sx={{ ml: 2 }}>
                                            {customer.name}
                                        </Typography>
                                    </Box>

                                    {customer.phone && (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                mb: 1,
                                            }}
                                        >
                                            <LocalPhoneIcon
                                                fontSize="small"
                                                sx={{ mr: 1 }}
                                            />
                                            <Typography variant="body2">
                                                {customer.phone}
                                            </Typography>
                                        </Box>
                                    )}

                                    {customer.address && (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "flex-start",
                                                mb: 1,
                                            }}
                                        >
                                            <PersonIcon
                                                fontSize="small"
                                                sx={{ mr: 1, mt: 0.5 }}
                                            />
                                            <Typography variant="body2">
                                                {customer.address}
                                            </Typography>
                                        </Box>
                                    )}
                                </CardContent>
                                <CardActions>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<ReceiptIcon />}
                                        fullWidth
                                        onClick={() =>
                                            handleViewReport(customer.id)
                                        }
                                    >
                                        View Report
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))
                )}
            </Grid>
        </Box>
    );
};

export default CustomerReportsList;
